import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, Loader2, Film, Image as ImageIcon, Music4, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const KIND_META = {
  image: { Icon: ImageIcon, accept: "image/png,image/jpeg,image/webp", hint: "PNG / JPG / WebP up to 8MB — logos & widget art" },
  video: { Icon: Film, accept: "video/mp4,video/webm", hint: "MP4 / WebM up to 60MB — backdrop loop" },
  audio: { Icon: Music4, accept: "audio/mpeg,audio/ogg", hint: "MP3 / OGG up to 15MB — loading music" },
};
const APPLY_LABEL = { image: "Set as logo", video: "Set as backdrop", audio: "Add to playlist" };

/* Media Library — owners upload their OWN images / video / music (copyright-safe)
   and apply them straight to the loading screen. Files are hosted by the hub, so
   they load inside CEF with zero hotlinking. */
export function MediaLibrary({ sid, onApplied }) {
  const [media, setMedia] = useState([]);
  const [kind, setKind] = useState("image");
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/nc/servers/${sid}/media`).then((r) => setMedia(r.data.media)).catch(() => {});
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/nc/servers/${sid}/media?kind=${kind}`, fd);
      toast.success(r.data.message);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setUploading(false); }
  };

  const apply = async (m) => {
    setBusyId(m.id);
    try {
      if (m.kind === "audio") {
        const cur = await api.get(`/nc/servers/${sid}/loading`);
        const audio = [...(cur.data.theme.audio || []), m.url].slice(-5);
        await api.patch(`/nc/servers/${sid}/theme`, { audio });
      } else if (m.kind === "video") {
        await api.patch(`/nc/servers/${sid}/theme`, { video_url: m.url });
      } else {
        await api.patch(`/nc/servers/${sid}/theme`, { logo_url: m.url });
      }
      toast.success("Applied — players see it on their next join");
      onApplied?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusyId(""); }
  };

  const remove = async (m) => {
    try {
      const r = await api.delete(`/nc/servers/${sid}/media/${m.id}`);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copy = (m) => navigator.clipboard?.writeText(`${window.location.origin}${m.url}`).then(() => toast.success("URL copied — paste it into an image widget")).catch(() => {});

  return (
    <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><UploadCloud className="w-3.5 h-3.5" /> Media library</p>
        <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">your own uploads — copyright-safe, hosted by the hub</span>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <span className={labelCls}>File type</span>
          <select data-testid="media-kind-select" className={`${inputCls} w-36`} value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.keys(KIND_META).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <span className={labelCls}>{KIND_META[kind].hint}</span>
          <input data-testid="media-file-input" ref={fileRef} type="file" accept={KIND_META[kind].accept} className={`${inputCls} file:mr-3 file:bg-nc-orange file:text-black file:border-0 file:px-3 file:py-1 file:font-mono file:text-[9px] file:uppercase file:cursor-pointer`} />
        </div>
        <button data-testid="media-upload-btn" disabled={uploading} onClick={upload} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors flex items-center gap-2">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload
        </button>
      </div>

      {media.length === 0 && <p className="font-mono text-[10px] text-zinc-600">Library empty — upload your server's logo, a backdrop loop, or your own loading track.</p>}

      {media.map((m) => {
        const { Icon } = KIND_META[m.kind] || KIND_META.image;
        return (
          <div key={m.id} data-testid={`media-row-${m.id.slice(0, 8)}`} className="border border-white/10 bg-[#070707] px-4 py-3 flex items-center gap-3 flex-wrap">
            <Icon className="w-4 h-4 text-nc-orange shrink-0" />
            <span className="font-mono text-[10px] text-zinc-200 break-all">{m.name}</span>
            <span className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 px-1.5 py-0.5">{m.kind}</span>
            <span className="font-mono text-[9px] text-zinc-600">{(m.size / 1048576).toFixed(1)}MB</span>
            <div className="ml-auto flex gap-1.5">
              <button data-testid={`media-apply-${m.id.slice(0, 8)}`} disabled={busyId === m.id} onClick={() => apply(m)} className="font-mono text-[8px] uppercase border border-nc-green/40 text-nc-green hover:bg-nc-green/10 px-2 py-1.5 transition-colors disabled:opacity-40 flex items-center gap-1">
                {busyId === m.id && <Loader2 className="w-3 h-3 animate-spin" />}{APPLY_LABEL[m.kind]}
              </button>
              {m.kind === "image" && <button data-testid={`media-copy-${m.id.slice(0, 8)}`} onClick={() => copy(m)} className="border border-white/15 p-1.5 text-zinc-400 hover:text-white transition-colors"><Copy className="w-3 h-3" /></button>}
              <button data-testid={`media-del-${m.id.slice(0, 8)}`} onClick={() => remove(m)} className="border border-red-500/40 p-1.5 text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
